export type MaskFunctions = {
  [key: string]: (value: string) => string;
};

export const genericMasks: MaskFunctions = {
  unmask: (value) => value.replace(/[^a-zA-Z0-9]/g, ""),
  unmaskCurrency: (value) =>
    value.replace(/[^a-zA-Z0-9]/g, "").replace(/R/g, ""),
  number: (value) => value.toString().replace(/\D/g, ""),
  state: (value) => value.replace(/\d/, ""),
};

export const dateTimeMasks: MaskFunctions = {
  hour: (value) =>
    String(value)
      .replace(/\D/, "")
      .replace(/(\d{2})(\d)/, "$1:$2"),
  showHour: (value) => {
    value = String(value);
    return value.length === 3
      ? value.replace(/(\d{1})(\d)/, "$1h$2")
      : value.replace(/(\d{2})(\d)/, "$1h$2");
  },
  date: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1/$2")
      .replace(/(\d{2})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1"),
};

export const documentMasks: MaskFunctions = {
  document: (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})/, "$1-$2")
        .replace(/(-\d{2})\d+?$/, "$1");
    } else {
      return digits
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d{1,2})/, "$1-$2")
        .replace(/(-\d{2})\d+?$/, "$1");
    }
  },
  cpf: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1"),
  rg: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{1})\d+?$/, "$1"),
  cnpj: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1"),
};

export const addressMasks: MaskFunctions = {
  zipcode: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{3})\d+?$/, "$1"),
};

export const phoneMasks: MaskFunctions = {
  phone: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .replace(/(-\d{5})\d+?$/, "$1"),
  cell: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{4})\d+?$/, "$1"),
};

export const currencyMasks: MaskFunctions = {
  currency: (value) => {
    const string = value.toString();

    if (string.replace(/\D/g, "") !== "")
      return (parseInt(string.replace(/\D/g, ""), 10) / 100).toLocaleString(
        "pt",
        {
          minimumFractionDigits: 2,
        }
      );

    return "0,00";
  },
  currencyAllPlatforms: (value) => {
    if (Number(value)) {
      const [currency, cents] = (Number(value) / 100)
        .toFixed(2)
        .toString()
        .split(".");

      return `R$ ${currency.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${cents}`;
    }

    return "R$ 0,00";
  },
};

/**
 * Converte um valor de campo de moeda para número decimal em reais.
 * Aceita tanto o formato mascarado pt-BR ("1.000,00") quanto o decimal cru
 * da API ("1000.00") — sempre devolve 1000. Vazio vira null.
 *
 * Com vírgula, usa a mesma regra da máscara `currency` (dígitos / 100): o input
 * controlado pode guardar o valor intermediário da digitação (ex.: "1.400,000"
 * enquanto a UI já mostra "14.000,00"). Trocar só a vírgula por ponto quebrava
 * isso para 1400.
 */
export function currencyToNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;

  const string = String(value).trim();
  if (!string) return null;

  if (string.includes(",")) {
    const digits = string.replace(/\D/g, "");
    if (!digits) return null;
    return parseInt(digits, 10) / 100;
  }

  const normalized = string.replace(/[^\d.-]/g, "");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Formata decimal (API ou número) como moeda BRL: "R$ 1.000,00". */
export function formatCurrency(value: unknown): string {
  const num = currencyToNumber(value);
  if (num === null) return "-";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export const percentageMasks: MaskFunctions = {
  percentage: (value) => {
    let number = Number(String(value).replace(/\D/g, ""));
    if (number > 100) number = 100;
    return `${number}`;
  },
};

export const creditCardMasks: MaskFunctions = {
  creditCard: (value) =>
    value
      .replace(/\D/g, "")
      .replace(/(\d{4})(\d)/, "$1 $2")
      .replace(/(\d{4})(\d)/, "$1 $2")
      .replace(/(\d{4})(\d)/, "$1 $2")
      .replace(/(.\d{4})\d+?$/, "$1"),
  creditCardDate: (value) => {
    value = value
      .replace(/\D/g, "")
      .replace(/([\d]{4})/, "$1")
      .replace(/([\d]{4})[\d]+?$/, "$1")
      .replace(/([\d]{2})([\d]{1})/, "$1/$2");
    const dates = value.split("/");
    if (dates.length) {
      if (parseInt(dates[0], 10) > 12) dates[0] = "12";
      return dates.join("/");
    }
    return value;
  },
  cvv: (value) =>
    value
      .replace(/[^\d]/g, "")
      .replace(/([\d]{4})/, "$1")
      .replace(/([\d]{4})[\d]+?$/, "$1"),
};

export const masks: MaskFunctions = {
  ...genericMasks,
  ...dateTimeMasks,
  ...documentMasks,
  ...addressMasks,
  ...phoneMasks,
  ...currencyMasks,
  ...percentageMasks,
  ...creditCardMasks,
};
